import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
} from '@tanstack/react-router';
import { AppShell } from './components/layout/AppShell';
import { DashboardRoute } from './routes/DashboardRoute';
import { BooksRoute } from './routes/BooksRoute';
import { BookEditorRoute } from './routes/BookEditorRoute';
import { MediaLibraryRoute } from './routes/MediaLibraryRoute';
import { SettingsRoute } from './routes/SettingsRoute';
import { DebugAiActionsRoute } from './routes/DebugAiActionsRoute';
import { EntitiesRoute } from './routes/EntitiesRoute';
import { EntityDetailRoute } from './routes/EntityDetailRoute';
import { SharedLibraryRoute } from './routes/SharedLibraryRoute';
import { ConsistencyRoute } from './routes/ConsistencyRoute';
import { WorldsRoute } from './routes/WorldsRoute';
import { WorldBuilderRoute } from './routes/WorldBuilderRoute';
import { OutlineRoute } from './routes/OutlineRoute';
import { StoryStudioRoute } from './routes/StoryStudioRoute';
import { BookResumeRoute } from './routes/BookResumeRoute';
import { BoardsRoute } from './routes/BoardsRoute';
import { StoryArchitectListRoute } from './routes/StoryArchitectListRoute';
import { StoryArchitectDetailRoute } from './routes/StoryArchitectDetailRoute';
import { OutlineListRoute } from './routes/OutlineListRoute';
import { OutlineDetailRoute } from './routes/OutlineDetailRoute';
import { BoardListRoute } from './routes/BoardListRoute';
import { BoardDetailRoute } from './routes/BoardDetailRoute';
import { StoryStudioListRoute } from './routes/StoryStudioListRoute';
import { StoryStudioSessionRoute } from './routes/StoryStudioSessionRoute';
import { SeriesListRoute } from './routes/SeriesListRoute';
import { SeriesDetailRoute } from './routes/SeriesDetailRoute';
import { ReferenceLibraryRoute } from './routes/ReferenceLibraryRoute';
import { EntityCreateWizardRoute } from './routes/EntityCreateWizardRoute';
import { TrainingQueueRoute } from './routes/TrainingQueueRoute';
import ProjectListRoute from './routes/ProjectListRoute';
import ProjectDashboardRoute from './routes/ProjectDashboardRoute';

const rootRoute = createRootRoute({
  component: () => (
    <AppShell>
      <Outlet />
    </AppShell>
  ),
});

const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: DashboardRoute,
});
const booksRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'books',
  component: BooksRoute,
});
const bookResumeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'books/$bookId/resume',
  component: BookResumeRoute,
});
const bookEditorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'books/$bookId',
  component: BookEditorRoute,
});
const bookConsistencyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'books/$bookId/consistency',
  component: ConsistencyRoute,
});
const bookOutlineRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'books/$bookId/outline',
  component: OutlineRoute,
});
const bookStoryStudioRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'books/$bookId/story-studio',
  component: StoryStudioRoute,
});
const bookBoardsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'books/$bookId/boards',
  component: BoardsRoute,
});
const imagesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'images',
  component: MediaLibraryRoute,
});
const entitiesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'entities',
  component: EntitiesRoute,
});
const entityCreateWizardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'entities/new',
  component: EntityCreateWizardRoute,
});
const entityDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'entities/$entityId',
  component: EntityDetailRoute,
});
const worldsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'worlds',
  component: WorldsRoute,
});
const worldBuilderRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'worlds/$worldId',
  component: WorldBuilderRoute,
});
const sharedLibraryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'libraries/shared',
  component: SharedLibraryRoute,
});
const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'settings',
  component: SettingsRoute,
});
const storyArchitectListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'story-architect',
  component: StoryArchitectListRoute,
});
const storyArchitectDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'story-architect/$sessionId',
  component: StoryArchitectDetailRoute,
});
const outlineListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'outlines',
  component: OutlineListRoute,
});
const outlineDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'outlines/$outlineId',
  component: OutlineDetailRoute,
});
const boardListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'boards',
  component: BoardListRoute,
});
const boardDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'boards/$boardId',
  component: BoardDetailRoute,
});
const storyStudioListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'story-studio',
  component: StoryStudioListRoute,
});
const storyStudioSessionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'story-studio/$sessionId',
  component: StoryStudioSessionRoute,
});
const seriesListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'series',
  component: SeriesListRoute,
});
const seriesDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'series/$seriesId',
  component: SeriesDetailRoute,
});
const referenceLibraryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'reference-library',
  component: ReferenceLibraryRoute,
});
const trainingQueueRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'training-queue',
  component: TrainingQueueRoute,
});
const projectListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'projects',
  component: ProjectListRoute,
});
const projectDashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'projects/$projectId',
  component: ProjectDashboardRoute,
});
const debugAiActionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'debug/ai-actions',
  component: DebugAiActionsRoute,
});

const routeTree = rootRoute.addChildren([
  dashboardRoute,
  booksRoute,
  bookResumeRoute,
  bookEditorRoute,
  bookConsistencyRoute,
  bookOutlineRoute,
  bookStoryStudioRoute,
  bookBoardsRoute,
  imagesRoute,
  entitiesRoute,
  entityCreateWizardRoute,
  entityDetailRoute,
  worldsRoute,
  worldBuilderRoute,
  sharedLibraryRoute,
  storyArchitectListRoute,
  storyArchitectDetailRoute,
  outlineListRoute,
  outlineDetailRoute,
  boardListRoute,
  boardDetailRoute,
  storyStudioListRoute,
  storyStudioSessionRoute,
  seriesListRoute,
  seriesDetailRoute,
  referenceLibraryRoute,
  trainingQueueRoute,
  projectListRoute,
  projectDashboardRoute,
  settingsRoute,
  debugAiActionsRoute,
]);

export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
